import React, { useContext } from "react";
import { Context } from "./../../Context";
import { AuthContext } from "@utils/DOIgnition/AuthContext";
import { IProps } from "./types";
import { useDispatch } from "react-redux";
import * as S from "./styles";
import C from "@assets/themes/colors";
import TextCommandBar from "./TextCommandBar";
import MainCommandBar from "./MainCommandBar";
import ImageCommandBar from "./ImageCommandBar";
import ShapeCommandBar from "./ShapeCommandBar";
import Button from "@components/Button";
import { requestAccess } from "@state/designs/utils";
import { hasAtLeastOneElementType, isLockedElement } from "@utils/editor";
import VersionCommandBar from "./VersionCommandBar";
import {
  DesignPermissionEnum,
  IDesign,
  IRequestAccess,
  PermissionEnum,
} from "@state/designs/types";
import actions from "@state/actions";
import { useParams } from "react-router";
import { isAuthenticated } from "../../../../../utils/userAccess";
import { ITemplate } from "@state/templates/types";

const CommandBars: React.FC<IProps> = (props) => {
  const context = useContext(Context);
  const authContext = useContext(AuthContext);
  const dispatch = useDispatch();
  const isLocked = isLockedElement(
    context.editorStatus.selectedElementsId,
    context.data
  );
  let { id } = useParams<any>();

  const [isRequesting, setIsRequesting] = React.useState(false);
  const [hasRequested, setHasRequested] = React.useState(false);  
   const templateInformation = context.entityInformation?.entityType === "TEMPLATE" ? (context.entityInformation as ITemplate) : undefined;
   const designInformation = context.entityInformation?.entityType === "DESIGN" ? (context.entityInformation as IDesign) : undefined;

  const [isRequestingView, setIsRequestingView] = React.useState(false);
  const [hasRequestedView, setHasRequestedView] = React.useState(false);

  const requestDesignAccess = async (permission: DesignPermissionEnum) => {
    if (permission == DesignPermissionEnum.EDIT) setIsRequesting(true);
    if (permission == DesignPermissionEnum.VIEW) setIsRequestingView(true);
    try {
      const userData = {
        email: authContext?.userData.profile.email
          ? authContext?.userData.profile.email
          : "defaultuser@tangocode.com",
        name: authContext?.userData.profile.name
          ? authContext?.userData.profile.name
          : "Default User",
      };
      const desginAccess: IRequestAccess = {
        design: context.entityInformation as IDesign,
        user: userData,
        permission,
      };
      const response = await requestAccess(dispatch, desginAccess);
      if (permission == DesignPermissionEnum.EDIT) {
        setIsRequesting(false);
        setHasRequested(true);
      }
      if (permission == DesignPermissionEnum.VIEW) {
        setIsRequestingView(false);
        setHasRequestedView(true);
      }
    } catch (error) {
      console.error(error);
      setIsRequesting(false);
      setIsRequestingView(false);
    }
  };

const duplicateTemplate = (id: string) => {
    return new Promise((resolve, reject) => {
      const userData = {
        email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
        name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User"
      };
      dispatch(actions.templates.duplicateTemplate({id: id,userData: userData}, (result: any, error: any) => {
        if (error) {
          reject(error);
        }
        else {
          resolve(result);
        }
      }))
    })
  }

  const duplicateDesign = (id: string, owner: string) => {
    return new Promise((resolve, reject) => {
      const userData = {
        email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
        name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User"
      };
      let keepFolder = owner===userData.email;

      dispatch(actions.designs.duplicateDesign({id: id,userData: userData, keepFolder: keepFolder, updateFolderCounter: keepFolder}, (result: any, error: any) => {
        if (error) {
          reject(error);
        }
        else {
          resolve(result);
        }
      }))
    })
  }

  const handleDuplicateTemplate = async () => {
    try {
      const templateDuplicated: any = await duplicateTemplate(templateInformation!.id);
      window.open(`/editor/template/${templateDuplicated.id}`);
    } catch (error) {
      context.onWarningScreenChange(
        true, "Create a Copy",
        `The template was not duplicated, please try later.`,
        "", undefined, "OKAY", "Duplicate template process failure",[]
      );
    }
  }

  const handleDuplicateDesign = async () => {
    try {
      const designDuplicated: any = await duplicateDesign(designInformation!.id, designInformation!.createdBy.email);
      window.open(`/editor/design/${designDuplicated.id}`);
    } catch (error) {
      context.onWarningScreenChange(
        true, "Create a Copy",
        `The design was not duplicated, please try later.`,
        "", undefined, "OKAY", "Duplicate design process failure",[]
      );
    }
  }

  const handleCreateACopy = () => {    
    if (templateInformation) {
      handleDuplicateTemplate();
    }
    else {
      handleDuplicateDesign();
    }
  }

  const handleViewLink = () => {
    const verifyAuth = isAuthenticated(authContext.userData);

    return (
      <button
        style={{
          backgroundColor: verifyAuth ? "#19325d" : "#6c757d",
          color: "#ffffff",
          width: "180px",
          height: "31px",
          margin: "0 0 0 42px",
          padding: "8px 19.5px 7px 19.5px",
          borderRadius: "5px",
          border: "solid 1px #8d8d8d",
          fontSize: "14px",
          fontWeight: "bold",
          textAlign: "center",
          transition: "background-color 0.3s ease, opacity 0.3s ease",
          opacity: verifyAuth ? "1" : "0.6",
          cursor: verifyAuth ? "pointer" : "not-allowed",
        }}
        disabled={!verifyAuth}
        onClick={() => {
          if (verifyAuth) {
            handleCreateACopy();
          }
        }}
      >
        Create a Copy
      </button>
    );
  };

  return (
    <S.Root>
      {context.userPermission === PermissionEnum.EDIT ? (
        <>
          {context.editorStatus.selectedElementsId.length > 0 && !isLocked && (
            <>
              {hasAtLeastOneElementType(
                context.editorStatus.selectedElementsId,
                context.data,
                "Text"
              ) && <TextCommandBar />}
              {hasAtLeastOneElementType(
                context.editorStatus.selectedElementsId,
                context.data,
                "Shape"
              ) && <ShapeCommandBar />}
              {hasAtLeastOneElementType(
                context.editorStatus.selectedElementsId,
                context.data,
                "Image"
              ) && <ImageCommandBar />}
            </>
          )}
          {context.editorStatus.editMode ? (
            <MainCommandBar />
          ) : (
            <VersionCommandBar />
          )}
        </>
      ) : (
        <S.OnlyViewContainer>
          <S.OnlyViewMessageContainer>
            <S.OnlyViewMessage>{handleViewLink()}</S.OnlyViewMessage>

            {context.userPermission !== PermissionEnum.PUBLIC_VIEW && (
              <S.Divider />
            )}
          </S.OnlyViewMessageContainer>

          {!isAuthenticated(authContext.userData) &&
            authContext.userData?.profile?.role !== "Staff" &&
            context.userPermission === PermissionEnum.NONE && (
              <Button
                label={
                  hasRequestedView
                    ? "REQUEST VIEW SENT"
                    : isRequestingView
                    ? "SENDING VIEW REQUEST..."
                    : "REQUEST TO VIEW"
                }
                style={{
                  hoverBackgroundColor: `${C.DARK_BLUE}`,
                  backgroundColor: "#00000000",
                  width: 190,
                  height: 12,
                }}
                onClick={() => requestDesignAccess(DesignPermissionEnum.VIEW)}
                disabled={isRequestingView || hasRequestedView}
              />
            )}

          {!isAuthenticated(authContext.userData) &&
            authContext.userData?.profile?.role !== "Staff" &&
            context.userPermission !== PermissionEnum.PUBLIC_VIEW && (
              <Button
                label={
                  hasRequested
                    ? "REQUEST SENT"
                    : isRequesting
                    ? "SENDING EDIT REQUEST..."
                    : context.userPermission === PermissionEnum.VIEW
                    ? "REQUEST EDIT ACCESS"
                    : "REQUEST TO EDIT"
                }
                style={{
                  hoverBackgroundColor: `${C.DARK_BLUE}`,
                  backgroundColor: "#00000000",
                  width: 170,
                  height: 12,
                }}
                onClick={() => requestDesignAccess(DesignPermissionEnum.EDIT)}
                disabled={isRequesting || hasRequested}
              />
            )}
        </S.OnlyViewContainer>
      )}
    </S.Root>
  );
};

export default CommandBars;
